/**
 * ReportDownload.jsx  —  Phase 3 Step 16
 *
 * "Download Report" button shown on the Data Analytics tab once results exist.
 * Lets the user pick Excel or PDF, fires the generate endpoint, polls until
 * ready, then triggers a browser file-download.
 */

import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Download, Loader2, ChevronDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ANALYTICS_API = `${BACKEND_URL}/api/analytics`;

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 40; // 2 minutes

const ReportDownload = ({ sessionId, mrsId, userId, hasResults }) => {
  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);
  const dropdownRef = useRef(null);

  if (!hasResults) return null;

  const generate = async (format) => {
    setOpen(false);
    setGenerating(true);
    setError(null);

    try {
      // 1. Request report generation
      const genRes = await axios.post(
        `${ANALYTICS_API}/reports/generate?user_id=${userId}`,
        {
          title: `Analytics Report`,
          mrs_id: mrsId,
          session_id: sessionId,
          include_all_session: true,
          include_result_ids: [],
          format,
        }
      );
      const reportId = genRes.data.report_id;
      if (!reportId) throw new Error('No report_id returned from server');

      // 2. Poll until ready or failed
      let attempts = 0;
      let statusData = null;
      await new Promise((resolve, reject) => {
        pollRef.current = setInterval(async () => {
          attempts++;
          if (attempts > POLL_MAX_ATTEMPTS) {
            clearInterval(pollRef.current);
            reject(new Error('Report generation timed out. Please try again.'));
            return;
          }
          try {
            const statusRes = await axios.get(
              `${ANALYTICS_API}/reports/${reportId}/download?user_id=${userId}`
            );
            const status = statusRes.data.status;
            if (status === 'ready') {
              clearInterval(pollRef.current);
              statusData = statusRes.data;
              resolve(statusRes.data);
            } else if (status === 'failed') {
              clearInterval(pollRef.current);
              reject(new Error(statusRes.data.error || 'Report generation failed.'));
            }
            // still 'generating' — keep polling
          } catch (pollErr) {
            clearInterval(pollRef.current);
            reject(pollErr);
          }
        }, POLL_INTERVAL_MS);
      });

      // 3. Trigger download — use the download_url from the status response,
      //    or fall back to the /file endpoint for local-dev mode.
      const resolvedDownloadUrl = statusData.download_url ||
        `${ANALYTICS_API}/reports/${reportId}/file?user_id=${userId}`;
      const dlRes = await axios.get(resolvedDownloadUrl, { responseType: 'blob' });
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      const filename = `analytics_report_${mrsId}_${Date.now()}.${ext}`;
      const blobUrl = URL.createObjectURL(dlRes.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setError(err.message || 'Failed to generate report. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="relative shrink-0">
      {error && (
        <p className="text-xs text-red-500 mb-1 px-1">{error}</p>
      )}

      {generating ? (
        <div className="flex items-center gap-1.5 text-xs text-blue-600 px-1 py-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          <span>Generating report…</span>
        </div>
      ) : (
        <div ref={dropdownRef} className="relative">
          <button
            onClick={() => setOpen((v) => !v)}
            className="w-full flex items-center justify-center gap-1.5 rounded-lg border border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-medium px-3 py-2 transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            Download Report
            <ChevronDown className={`w-3 h-3 ml-auto transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>

          {open && (
            <div className="absolute bottom-full mb-1 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-10">
              <button
                onClick={() => generate('excel')}
                className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <span className="text-green-600 font-bold text-sm">X</span>
                Excel (.xlsx)
              </button>
              <button
                onClick={() => generate('pdf')}
                className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <span className="text-red-500 font-bold text-sm">P</span>
                PDF (.pdf)
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReportDownload;
